'use client';

import { useEffect, useMemo } from 'react';
import Disclosure from './Disclosure';
import { DisclosureProps } from 'libs/contentful/types';
import { useDisclosureContext, useConsumedDisclosures, useDisclosureNumberingMap } from 'contexts/DisclosureProvider';
import { AnalyticsModuleTrackingProvider } from 'components/analytics/AnalyticsModuleTracking';

interface DynamicDisclosuresProps {
  baseline?: DisclosureProps[];
}

/**
 * Renders the page's footer disclosures: registers them with the disclosure
 * provider and filters out any consumed by DisclosureAddOn modules.
 */
const DynamicDisclosures = ({ baseline = [] }: DynamicDisclosuresProps) => {
  const { registerDisclosures } = useDisclosureContext();
  const { consumedDisclosureIds } = useConsumedDisclosures();
  const disclosureNumberingMap = useDisclosureNumberingMap();

  useEffect(() => {
    registerDisclosures({
      moduleId: 'variant-aware-disclosures',
      disclosures: baseline,
      source: 'page-disclosures',
    });
  }, [baseline, registerDisclosures]);

  // Filter out disclosures that have been consumed by DisclosureAddOn modules
  const disclosures = useMemo(() => {
    return baseline.filter(disclosure => {
      const disclosureId = disclosure.sys?.id;
      if (!disclosureId) return true; // Keep disclosures without IDs
      return !consumedDisclosureIds.has(disclosureId);
    });
  }, [baseline, consumedDisclosureIds]);

  if (!disclosures.length) return null;

  return (
    <AnalyticsModuleTrackingProvider
      initialAttributes={{
        moduleType: 'dynamic-disclosures',
        moduleTitle: 'Dynamic Disclosures',
        moduleIndex: '1',
        // Detects the browser at runtime; there is no value to parse at a boundary here.
        // oxlint-disable-next-line anti-slop/no-runtime-typeof
        pageTitle: typeof document !== 'undefined' ? document.title : '',
      }}>
      <div className="mb-2 last:mb-0 dynamic-disclosures">
        <div className="content-wrapper">
          <div className="flex flex-col gap-y-2">
            {disclosures.map((disclosure, index) => {
              // Get auto number from the global numbering map to maintain consistency
              const disclosureId = disclosure.sys?.id;
              const autoNumber =
                !disclosure.symbol && disclosureId ? disclosureNumberingMap.get(disclosureId) : undefined;

              return <Disclosure key={disclosureId || index} {...disclosure} autoNumber={autoNumber} />;
            })}
          </div>
        </div>
      </div>
    </AnalyticsModuleTrackingProvider>
  );
};

export default DynamicDisclosures;
